<template>
	<el-row>
		<el-col :span="24">
			<div class="base-form header">
				<h2>Сервисные работы</h2>
				<el-button type="primary" @click="onAddJobClick" :icon="circleIcon" size="large">
					Новая сервисная работа
				</el-button>
			</div>
		</el-col>
	</el-row>
	<el-row>
		<el-col :span="6">
			<div class="base-form filter">
				<el-input v-model="tableSearchQuery"
					placeholder="Поиск по компонентам, категориям, операциям и группам работ" clearable class="input-with-select">
					<template #append>
						<el-button :icon="searchIcon" />
					</template>
				</el-input>
			</div>
		</el-col>
	</el-row>
	<el-row :gutter="24">
		<el-col :span="4">
			<div class="base-form filter">
				<component-groups-tree-filter-component
					@update:modelValue="onTableComponentGroupsFilterUpdate"
					v-model="tableComponentGroupsFilter" />
			</div>
		</el-col>
		<el-col :span="4">
			<div class="base-form filter">
				<el-select
					v-model="tableCategoriesFilter"
					@change="fetchJobsAsync"
					multiple
					clearable
					filterable
					placeholder="Фильтр по категориям">
					<el-option
						v-for="item in serviceJobCategories"
						:key="item.value"
						:label="item.label"
						:value="item.value"
					/>
				</el-select>
			</div>
		</el-col>
		<el-col :span="4">
			<div class="base-form filter">
				<el-select
					v-model="tableOperationsFilter"
					@change="fetchJobsAsync"
					multiple
					clearable
					filterable
					placeholder="Фильтр по операциям">
					<el-option
						v-for="item in serviceJobOperations"
						:key="item.value"
						:label="item.label"
						:value="item.value"
					/>
				</el-select>
			</div>
		</el-col>
		<el-col :span="4">
			<div class="base-form filter">
				<el-select
					v-model="tableGroupsFilter"
					@change="fetchJobsAsync"
					multiple
					clearable
					filterable
					placeholder="Фильтр по группе работ">
					<el-option
						v-for="item in serviceJobGroups"
						:key="item.value"
						:label="item.label"
						:value="item.value"
					/>
				</el-select>
			</div>
		</el-col>
	</el-row>
	<el-row :gutter="24">
		<el-col>
			<div class="base-form filter">
				<div style="display: table; height: 100%; width: 100%">
					<div style="display: table-cell; vertical-align: baseline; width: 20px">
						<el-icon :size="18" style="margin: .32rem 0 0"><CircleClose :color="isFiltersEnabled() ? 'var(--el-color-primary)' : 'var(--el-color-info-light-5)'" /></el-icon>
					</div>
					<div style="display: table-cell; vertical-align: middle; padding-left: .56rem;">
						<el-link :disabled="!isFiltersEnabled()" :type="isFiltersEnabled() ? 'primary' : 'info'" :underline="false" @click="onClearFiltersButtonClick">
							Очистить фильтр
						</el-link>				
					</div>
				</div>
			</div>
		</el-col>
	</el-row>
	<el-row>
		<el-col :span="24">
			<div>
				<el-table row-key="id" v-loading="tableLoading" sortable="custom" :data="tableData"
					:default-sort="{ prop: tableSortColumn, order: tableSortOrder }"
					@sort-change="onTableSortingChange"
					@row-click="onTableRowClick">
					<template #empty>
						Список сервисных работ пуст, попробуйте изменить фильтр
					</template>
					<el-table-column type="selection" />
					<el-table-column label="Компонент" prop="componentname" sortable column-key="componentname">
						<template #default="scope">
							{{ scope.row.component.name }}
							<div>
								<el-text type="info">{{ scope.row.component.componentGroup?.name }}</el-text>
							</div>
						</template>
					</el-table-column>
					<el-table-column label="Операция" prop="operation.name" />
					<el-table-column label="Категория" prop="category.name" sortable column-key="categoryname" />
					<el-table-column label="Стоимость НЧ (₽)" prop="category.costPerHour" sortable column-key="categorycostperhour" />
					<el-table-column label="Трудозатраты (час)" prop="estimationHrs" />
					<el-table-column label="Группа работ" prop="groupname" sortable column-key="groupname">
						<template #default="scope">						
							{{ scope.row.group?.name ?? '' }}
						</template>
					</el-table-column>
					<el-table-column width="120" align="right">
						<template #default="scope">
							<el-button size="small" type="danger" @click="(e) => onDeleteJobClick(e, scope.row.id)">
								Удалить
							</el-button>
						</template>
					</el-table-column>
				</el-table>
			</div>
		</el-col>
	</el-row>
</template>

<script lang="ts">
import { Options, Vue } from 'vue-class-component';
import { CirclePlus, CircleClose, Search } from '@element-plus/icons-vue';
import { ElButton, ElTable, ElLink, ElMessageBox, ElNotification } from 'element-plus';
import axiosHelper from '@/helpers/axiosHelper';
import store from '@/store';
import eventHelper from '@/helpers/eventsHelper';
import TabsDictionary from '@/variables/tabs';
import { WatchStopHandle, watch } from 'vue';
import ApplicationTab from '@/models/ApplicationTab';
import ServiceJobModel from '@/models/ServiceJobModel';
import ServiceJobCategoryModel from '@/models/ServiceJobCategoryModel';
import ServiceJobOperationModel from '@/models/ServiceJobOperationModel';
import ServiceJobGroupModel from '@/models/ServiceJobGroupModel';
import LabelValuePair from '@/models/LabelValuePair';
import ComponentGroupsTreeFilterComponent from '@/components/filters/ComponentGroupsTreeFilterComponent.vue';

@Options({
	components: {
		CirclePlus, ElButton, ElTable, ElLink, CircleClose,
		ComponentGroupsTreeFilterComponent
	}
})
export default class ServiceJobsView extends Vue {
	/** constants */
	circleIcon = CirclePlus;
	searchIcon = Search;


	/** view-models */
	tableLoading = false;
	tableLoadingSvg = `<path class="path" d="M 30 15 L 28 17 M 25.61 25.61 A 15 15, 0, 0, 1, 15 30 A 15 15, 0, 1, 1, 27.99 7.5 L 15 15" style="stroke-width: 4px; fill: rgba(0, 0, 0, 0)"/>`;
	tableSortColumn = 'category.name';
	tableSortOrder = 'ascending';
	
	tableData: Array<ServiceJobModel> = [];
	tableSearchQuery = '';
	tableCategoriesFilter: Array<number> = [];
	serviceJobCategories: Array<LabelValuePair<string, number>> = [];

	tableOperationsFilter: Array<number> = [];
	serviceJobOperations: Array<LabelValuePair<string, number>> = [];

	tableComponentGroupsFilter: Array<LabelValuePair<string, number>> = [];

	tableGroupsFilter: Array<number> = [];
	serviceJobGroups: Array<LabelValuePair<string, number>> = [];

	watchTableSearchQuery: WatchStopHandle | null = null;

	mounted() {
		this.fetchJobsAsync();
		this.fetchJobCategoriesAsync();
		this.fetchJobOperationsAsync();
		this.fetchJobGroupsAsync();

		this.watchTableSearchQuery = watch(
			() => this.tableSearchQuery,
			() => this.fetchJobsAsync()
		);

		eventHelper.setEventListener('jobsUpdate', this.fetchJobsAsync);
		eventHelper.setEventListener('jobCategoriesUpdate', this.fetchJobCategoriesAsync);
		eventHelper.setEventListener('jobOperationsUpdate', this.fetchJobOperationsAsync);
		eventHelper.setEventListener('jobGroupsUpdate', this.fetchJobGroupsAsync);
	}

	unmounted() {
		if (this.watchTableSearchQuery) {
			this.watchTableSearchQuery();
		}

		eventHelper.removeEventListener('jobsUpdate', this.fetchJobsAsync);
		eventHelper.removeEventListener('jobCategoriesUpdate', this.fetchJobCategoriesAsync);
		eventHelper.removeEventListener('jobOperationsUpdate', this.fetchJobOperationsAsync);
		eventHelper.removeEventListener('jobGroupsUpdate', this.fetchJobGroupsAsync);
	}

	onAddJobClick() {
		const addJobTab = TabsDictionary.jobAdd;
		const isAddingNewJobForm = true;
		addJobTab.params = [ null, isAddingNewJobForm ];
		store.commit('addTab', addJobTab);
		const newTab: ApplicationTab = store.getters.newlyAddedTab;
		store.commit('changeTab', newTab.id);
	}

	async fetchJobsAsync() {
		try {
			this.tableLoading = true;
			const filterObject = {
				operations: this.tableOperationsFilter?.length ? this.tableOperationsFilter.join(',') : null,
				componentGroups: this.parseComponentGroupsFilter(),
				categories: this.tableCategoriesFilter?.length ? this.tableCategoriesFilter.join(',') : null,
				groups: this.tableGroupsFilter?.length ? this.tableGroupsFilter.join(',') : null,
				orderBy: this.tableSortColumn ? this.tableSortColumn : 'categoryname',
				orderAsc: this.tableSortOrder == 'ascending' ? true : false,
				query: this.tableSearchQuery,
			};
			const jwt = store.getters.jwt;
			const usersHttpClient = axiosHelper.createInstance(jwt);
			const response = await usersHttpClient.get('/api/servicejobs', { params: filterObject });						
			this.tableData = response.data.map((j: any) => new ServiceJobModel(j));
		} catch (e: any) {
			if (e.code == 'ERR_NETWORK') {
				ElNotification({
					title: 'Ошибка',
					message: `Ошибка соединения, не удалось загрузить список работ`,
					type: 'error',
				});
			} else {
				ElNotification({
					title: 'Ошибка',
					message: `Ошибка загрузка списка работ`,
					type: 'error',
				});
			}
			console.error('Error loading jobs list', e)
		} finally {
			this.tableLoading = false;
		}
	}

	onDeleteJobClick(event: Event, jobId: number) {
		event.stopPropagation();
		ElMessageBox.confirm(
			'Сервисная работа будет удалена', {
			confirmButtonText: 'OK',
			cancelButtonText: 'Отмена',
			type: 'error',
		})
		.then(() => this.deleteJobAsync(jobId));
	}

	onTableSortingChange(a: any) {
		this.tableSortColumn = a.prop ?? null;
		this.tableSortOrder == a.order;
		this.fetchJobsAsync();
	}

	onTableRowClick(job: ServiceJobModel) {
		const viewJobTab = TabsDictionary.jobView;
		const isAddingNewJobForm = false;
		viewJobTab.params = [ job, isAddingNewJobForm ];
		store.commit('addTab', viewJobTab);
		const newTab: ApplicationTab = store.getters.newlyAddedTab;
		store.commit('changeTab', newTab.id);
	}

	onTableComponentGroupsFilterUpdate(componentGroups: Array<LabelValuePair<string, number>>) {
		this.tableComponentGroupsFilter = componentGroups;
		this.fetchJobsAsync();
	}				

	async fetchJobCategoriesAsync() {
		try {
			const filterObject = {
				orderBy: 'name',
				orderAsc: true,
			};
			const jwt = store.getters.jwt;
			const usersHttpClient = axiosHelper.createInstance(jwt);
			const response = await usersHttpClient.get('/api/servicejobcategories', { params: filterObject });
			this.serviceJobCategories = response.data.map(
				(cat: ServiceJobCategoryModel) => new LabelValuePair({label: `${cat.name} ${cat.costPerHour ? `(${cat.costPerHour} р.)` : ''}`, value: cat.id})
			);			
		} catch (e: any) {
			console.error('Error loading service-job categories', e);
		}
	}

	async fetchJobOperationsAsync() {
		try {
			const filterObject = {
				orderBy: 'name',
				orderAsc: true,
			};
			const jwt = store.getters.jwt;
			const usersHttpClient = axiosHelper.createInstance(jwt);
			const response = await usersHttpClient.get('/api/servicejoboperations', { params: filterObject });
			this.serviceJobOperations = response.data.map(
				(op: ServiceJobOperationModel) => new LabelValuePair({label: op.name, value: op.id})
			);
		} catch (e: any) {
			console.error('Error loading service-job operations', e);
		}
	}

	async fetchJobGroupsAsync() {
		try {
			const filterObject = {
				orderBy: 'name',
				orderAsc: true,
			};
			const jwt = store.getters.jwt;
			const usersHttpClient = axiosHelper.createInstance(jwt);
			const response = await usersHttpClient.get('/api/servicejobgroups', { params: filterObject });
			this.serviceJobGroups = response.data.map((g: ServiceJobGroupModel) => new LabelValuePair({label: g.name, value: g.id}));
		} catch (e: any) {
			console.error('Error loading service-job groups', e);
		}
	}

	async deleteJobAsync(jobId: number) {
		try {
			this.tableLoading = true;
			const jwt = store.getters.jwt;
			const a = axiosHelper.createInstance(jwt);
			await a.delete(`/api/servicejobs/${jobId}`);
			ElNotification({
				title: 'Работа удалена',
				type: 'success',
			});
			await this.fetchJobsAsync();
		} catch (e) {
			ElNotification({
				title: 'Ошибка, работа не была удалена',
				type: 'error',
			});
			console.error(`Error deleting service-job #${jobId}`, e)
		} finally {
			this.tableLoading = false;
		}
	}

	parseComponentGroupsFilter() : string | null {
		if (!this.tableComponentGroupsFilter?.length) {
			return null;
		}

		const filteredComponentsIds = new Array<number>();

		this.tableComponentGroupsFilter
			.map(component => filteredComponentsIds.push(component.value));

		return filteredComponentsIds.length ? filteredComponentsIds.join(',') : null;
	}

	onClearFiltersButtonClick() {
		this.tableCategoriesFilter = [];
		this.tableOperationsFilter = [];
		this.tableComponentGroupsFilter = [];
		this.tableGroupsFilter = [];

		if (this.tableSearchQuery) {
			this.tableSearchQuery = '';
		} else {
			eventHelper.dispatchEvent('jobsUpdate');
		}
	}

	isFiltersEnabled() {
		return this.tableCategoriesFilter?.length ||
				this.tableOperationsFilter?.length ||
				this.tableComponentGroupsFilter?.length  ||
				this.tableGroupsFilter?.length  || 
				this.tableSearchQuery;
	}
}
</script>